import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Genre,
  GetUserRewindQuery,
  Maybe,
  UserRewindMeFragmentFragment
} from 'types/graphql-api.generated';

import { getUserRewind } from 'common/api/UserRewind';
import { MissingScreenType } from 'common/components/RewindModal/helper';
import loadOpes, {
  RewindConfig,
  RewindImages,
  SuggestedEntitiesList
} from 'common/opes/loadOpes';
import trans from 'common/tools/translations/trans';

export const useOpeCustomization = () => {
  const [genres, setGenres] = useState<Record<Genre, string>>();
  const [images, setImages] = useState<RewindImages>();
  const [loaded, setLoaded] = useState(false);
  const [ope, setOpe] = useState<RewindConfig>();

  useEffect(() => {
    loadOpes().then(({ config, images, ...rewind }) => {
      setOpe({
        ...config,
        main: {
          color: '#F2F2F2',
          font: '"Montserrat", Arial, sans-serif',
          ...(config?.main ?? {})
        }
      });
      setImages(images);
      setGenres(rewind['translations-graph_genres']);
      setLoaded(true);
    });
  }, []);

  return { genres, images, loaded, ope };
};

export const useNavigation = (initialScreens: ScreenNames[] = []) => {
  const [missingScreens, setMissingScreens] = useState<MissingScreenType[]>();
  const [screens, _setScreens] = useState<ScreenNames[]>(initialScreens);
  const [step, setStep] = useState(0);

  const steps = useMemo(() => screens.length, [screens]);
  const screenName = useMemo(() => screens[step], [screens, step]);

  const handleNext = useCallback(() => {
    setStep(currentStep => Math.min(currentStep + 1, steps - 1));
  }, [steps]);

  const handleStep = useCallback(
    (newStep: number) => {
      const validStep = Math.max(0, Math.min(newStep, steps - 1));
      setStep(validStep);
    },
    [steps]
  );

  const setScreens = useCallback((newScreens: ScreenNames[]) => {
    _setScreens(newScreens);
    setStep(0);
  }, []);

  return {
    handleNext,
    handleStep,
    missingScreens,
    screenName,
    screens,
    setMissingScreens,
    setScreens,
    step,
    steps
  };
};

export const useUserData = (
  { release_from, release_to }: SuggestedEntitiesList | undefined = {
    release_from: undefined,
    release_to: undefined
  }
) => {
  const [userData, setUserData] = useState<Maybe<GetUserRewindQuery>>();
  const [dataLoadingStatus, setDataLoadingStatus] = useState<
    'loading' | 'loaded' | 'error'
  >();
  const loadData = useRef(false);

  const getData = useCallback(async () => {
    if (!loadData.current) {
      loadData.current = true;
      setDataLoadingStatus('loading');
      const data = await getUserRewind(release_from, release_to);
      setUserData(data);
    }
  }, [release_from, release_to]);

  return {
    dataLoadingStatus,
    getData,
    setDataLoadingStatus,
    userData
  };
};

export const useUserValues = (extra?: Maybe<UserRewindMeFragmentFragment>) => {
  const userNickname = useMemo(() => extra?.nickname, [extra?.nickname]);
  const userSubscriptionDate = useMemo(
    () =>
      extra?.creationDate
        ? trans('rewind.user_score.indicative', {
            count: new Date(extra.creationDate).getFullYear()
          })
        : null,
    [extra?.creationDate]
  );

  return { userNickname, userSubscriptionDate };
};

const MAX_PRECISE_HOUR = 1;
const transKeyHours = 'rewind.viewing.hours';
const transKeyMinutes = 'rewind.viewing.minutes';
const transKeyHoursShort = 'rewind.viewing.hours.short';
const transKeyMinutesShort = 'rewind.viewing.minutes.short';

export const useGetDisplayTime = (
  minutes?: number | null,
  shortTimeLabel: boolean = false
) => {
  const totalDuration = useMemo(() => minutes ?? 0, [minutes]);

  const currentTransKeyHours = shortTimeLabel
    ? transKeyHoursShort
    : transKeyHours;
  const currentTransKeyMinutes = shortTimeLabel
    ? transKeyMinutesShort
    : transKeyMinutes;

  const totalHours = useMemo(
    () => Math.floor(totalDuration / 60),
    [totalDuration]
  );
  const totalMinutes = useMemo(
    () => totalDuration - totalHours * 60,
    [totalDuration, totalHours]
  );

  // we round up to the next hour
  const displayHour = useMemo(
    () =>
      totalHours > MAX_PRECISE_HOUR
        ? totalHours + (totalMinutes > 0 ? 1 : 0)
        : totalHours,
    [totalHours, totalMinutes]
  );

  const bigNumber = useMemo(
    () =>
      totalHours > 0
        ? totalHours < MAX_PRECISE_HOUR
          ? `${totalHours} ${trans(currentTransKeyHours, { count: totalHours })}`
          : displayHour
        : totalMinutes,
    [displayHour, totalHours, totalMinutes, currentTransKeyHours]
  );

  const timeLabel = useMemo(
    () =>
      totalHours >= MAX_PRECISE_HOUR
        ? trans(currentTransKeyHours, { count: totalHours })
        : totalHours === 0
          ? trans(currentTransKeyMinutes, { count: totalMinutes })
          : `${totalMinutes} ${trans(currentTransKeyMinutes, { count: totalMinutes })}`,
    [totalHours, totalMinutes, currentTransKeyHours, currentTransKeyMinutes]
  );

  return { totalDuration, bigNumber, timeLabel };
};
